import { Link } from "react-router-dom";
import type { Topic } from "@/types/content";
import { formatDateC, getCategoryName } from "@/lib/content";

interface TopicCardProps {
  topic: Topic;
  variant?: "grid" | "list";
}

export default function TopicCard({ topic, variant = "grid" }: TopicCardProps) {
  const categoryName = getCategoryName(topic.category);

  if (variant === "list") {
    return (
      <Link
        to={`/topics/${topic.slug}`}
        className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-background-100 sm:flex-row sm:items-center md:p-5"
      >
        <div className="h-40 w-full overflow-hidden rounded-md bg-background-200 sm:h-24 sm:w-36 sm:shrink-0">
          <img
            src={topic.cover}
            alt={`${topic.title} 封面`}
            title={`${topic.title} - 黑料吃话文字整理`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-medium text-secondary-900">
              {categoryName}
            </span>
            <span className="whitespace-nowrap">{formatDateC(topic.date)}</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line" />
              {topic.views}
            </span>
          </div>
          <h3 className="mt-2.5 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
            {topic.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {topic.summary}
          </p>
        </div>
        <i className="ri-arrow-right-line hidden text-lg text-foreground-400 transition-colors group-hover:text-primary-500 sm:block" />
      </Link>
    );
  }

  return (
    <Link
      to={`/topics/${topic.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-48 w-full overflow-hidden bg-background-200">
        <img
          src={topic.cover}
          alt={`${topic.title} 封面`}
          title={`${topic.title} - 黑料吃话文字整理`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        {topic.hot ? (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-line" />
            热议
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-foreground-500">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-medium text-secondary-900">
            {categoryName}
          </span>
          <span className="whitespace-nowrap">{formatDateC(topic.date)}</span>
        </div>
        <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
          {topic.title}
        </h3>
        <p className="mt-2.5 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
          {topic.summary}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-time-line" />
            {topic.readTime}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line" />
            {topic.views} 阅读
          </span>
        </div>
      </div>
    </Link>
  );
}